import { uiPaper } from "shared/ui/uiSurface"
import { ContentCopy } from "@mui/icons-material"
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Employee,
  useCreateEmployeeMutation,
  useUpdateEmployeeMutation,
} from "../../apis/appApi"

type EmployeeFormDialogProps = {
  open: boolean
  onClose: () => void
  employee?: Employee | null
}

export const EmployeeFormDialog: FC<EmployeeFormDialogProps> = ({
  open,
  onClose,
  employee,
}) => {
  const { t } = useTranslation()
  const isEdit = employee != null

  const [createEmployee, { isLoading: isCreating }] =
    useCreateEmployeeMutation()
  const [updateEmployee, { isLoading: isUpdating }] =
    useUpdateEmployeeMutation()

  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [username, setUsername] = useState("")
  const [sharedAccess, setSharedAccess] = useState(false)
  const [administrator, setAdministrator] = useState(false)
  const [enabled, setEnabled] = useState(true)
  const [tempPassword, setTempPassword] = useState<string | null>(null)

  useEffect(() => {
    if (isEdit && employee) {
      setFirstName(employee.firstName)
      setLastName(employee.lastName)
      setEnabled(employee.enabled)
    } else if (!isEdit && open) {
      setFirstName("")
      setLastName("")
      setUsername("")
      setSharedAccess(false)
      setAdministrator(false)
      setEnabled(true)
      setTempPassword(null)
    }
  }, [isEdit, employee, open])

  const handleClose = () => {
    setTempPassword(null)
    onClose()
  }

  const handleSubmit = async () => {
    try {
      if (isEdit) {
        await updateEmployee({
          id: employee!.id,
          firstName,
          lastName,
          enabled,
        }).unwrap()
        handleClose()
      } else {
        const result = await createEmployee({
          firstName,
          lastName,
          username: username.toLowerCase(),
          sharedAccess,
          administrator,
        }).unwrap()
        const pwd = result?.data?.tmpPassword ?? result?.tmpPassword
        if (pwd) {
          setTempPassword(pwd)
        } else {
          handleClose()
        }
      }
    } catch (error) {
      console.error("Failed to save employee:", error)
    }
  }

  const isValid = isEdit
    ? firstName.trim() !== "" && lastName.trim() !== ""
    : firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      username.trim() !== ""
  const isSaving = isCreating || isUpdating

  // Show temp password view after successful creation
  if (tempPassword) {
    return (
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-116") }}
        data-testid="employee-temp-password-dialog"
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("employees.tempPassword")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              {t("employees.tempPasswordMessage")}
            </Typography>
            <TextField
              data-testid="employee-temp-password-input"
              value={tempPassword}
              fullWidth
              size="small"
              slotProps={{
                input: {
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        data-testid="employee-temp-password-copy-btn"
                        size="small"
                        onClick={() =>
                          navigator.clipboard.writeText(tempPassword)
                        }
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Alert severity="warning">
              {t("employees.tempPasswordMessage")}
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            data-testid="employee-temp-password-close-btn"
            onClick={handleClose}
            variant="contained"
          >
            {t("close")}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-115") }}
      data-testid="employee-form-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEdit ? t("employees.edit") : t("employees.create")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            data-testid="employee-firstName-input"
            label={t("employees.firstName")}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            fullWidth
            size="small"
            autoFocus
          />
          <TextField
            data-testid="employee-lastName-input"
            label={t("employees.lastName")}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            fullWidth
            size="small"
          />
          {!isEdit && (
            <>
              <TextField
                data-testid="employee-username-input"
                label={t("employees.username")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                fullWidth
                size="small"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="employee-sharedAccess-checkbox"
                    checked={sharedAccess}
                    onChange={(e) => setSharedAccess(e.target.checked)}
                  />
                }
                label={t("employees.sharedAccess")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    data-testid="employee-administrator-checkbox"
                    checked={administrator}
                    onChange={(e) => setAdministrator(e.target.checked)}
                  />
                }
                label={t("employees.administrator")}
              />
            </>
          )}
          {isEdit && (
            <FormControlLabel
              control={
                <Switch
                  data-testid="employee-enabled-switch"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label={t("employees.enabled")}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button data-testid="employee-form-cancel-btn" onClick={handleClose}>
          {t("cancel")}
        </Button>
        <Button
          data-testid="employee-form-submit-btn"
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValid || isSaving}
        >
          {isEdit ? t("employees.editSave") : t("employees.create")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
